import { useState } from "react";
import { Dialog, Tooltip } from "@mui/material";
import CompareDialog from "../quality_check/CompareDialog";
import { factoryPictures, isVideo } from "../quality_check/media";
import styles from "./extraInfo.module.css";

const VIDEO_TYPE = (mime) => mime?.startsWith("video");

function Thumb({ item, label }) {
  return (
    <a className={styles.thumb} href={item.url} target="_blank" rel="noreferrer" aria-label={`Open ${label}`}>
      {isVideo(item) ? <span className={styles.play}>▶</span> : <img src={item.url} alt="" loading="lazy" />}
    </a>
  );
}

function Section({ title, requested, children, empty }) {
  if (!requested) return null;
  return (
    <section className={styles.section}>
      <div className={styles.label}>{title}</div>
      {children || <p className={styles.missing}>{empty}</p>}
    </section>
  );
}

// What the team sent back for an extra request: factory pictures, sizing
// table and their note, in one quiet sheet.
export function ExtraInfoDialog({ product: initial, open, onClose }) {
  const [product, setProduct] = useState(initial);
  const [comparing, setComparing] = useState(false);
  const options = product?.quotation_request?.options || {};
  const notes = product?.quotation_request?.additional_notes || {};
  const pictures = factoryPictures(product);
  const sizing =
    product?.sizing_table && product.sizing_table !== "Not available"
      ? { url: product.sizing_table, mime_type: options.option2MimeType }
      : null;

  if (comparing) {
    return (
      <CompareDialog
        product={product}
        onClose={() => {
          setComparing(false);
          onClose();
        }}
        onRequested={(updated) => updated && setProduct((p) => ({ ...p, ...updated }))}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: styles.paper }}>
      <header className={styles.head}>
        {product?.images?.[0]?.src && <img className={styles.cover} src={product.images[0].src} alt="" />}
        <div>
          <h2>{product?.title}</h2>
          <p>Extra information from our team</p>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div className={styles.body}>
        <Section
          title="Factory pictures"
          requested={Boolean(options.option1) || pictures.length > 0}
          empty="The factory couldn't provide pictures for this product."
        >
          {pictures.length > 0 && (
            <>
              <div className={styles.grid}>
                {pictures.map((item, i) => (
                  <Thumb key={item.url} item={item} label={`factory picture ${i + 1}`} />
                ))}
              </div>
              <button type="button" className={styles.compare} onClick={() => setComparing(true)}>
                Compare with your listing
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </>
          )}
        </Section>

        <Section
          title="Sizing table"
          requested={Boolean(options.option2) || Boolean(sizing)}
          empty="The factory couldn't provide a sizing table for this product."
        >
          {sizing &&
            (VIDEO_TYPE(sizing.mime_type) ? (
              <div className={styles.grid}>
                <Thumb item={sizing} label="sizing table" />
              </div>
            ) : (
              <a className={styles.sizing} href={sizing.url} target="_blank" rel="noreferrer">
                <img src={sizing.url} alt="Sizing table" loading="lazy" />
              </a>
            ))}
        </Section>

        {(notes.client_note || notes.admin_note) && (
          <section className={styles.section}>
            <div className={styles.label}>Notes</div>
            {notes.client_note && (
              <div className={styles.note}>
                <span>You</span>
                {notes.client_note}
              </div>
            )}
            {notes.admin_note && (
              <div className={`${styles.note} ${styles.team}`}>
                <span>U-Send</span>
                {notes.admin_note}
              </div>
            )}
          </section>
        )}
      </div>
    </Dialog>
  );
}

// Small badge on the product card: extra information is ready to view.
export default function ExtraInfoBadge({ product, onOpen }) {
  return (
    <Tooltip title="Extra information ready" placement="top" arrow>
      <button type="button" className={styles.badge} onClick={onOpen} aria-label="View extra information">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="M21 16l-5-5-8 8" />
        </svg>
      </button>
    </Tooltip>
  );
}
